import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';

import { observer } from 'mobx-react';
import moment from 'moment';
import { Dialog } from '@mui/material';

import { FileType } from '../../../../chat/utils/files';
import { ClientDocumentSource } from '../../../../chat/Model/ClientDocument';
import { InfoTag } from '../../../Tags';
import useStorage from '../../../../chat/hooks/useStorage';
import useComponentMounted from '../../../../chat/hooks/useComponentMounted';
import { formatFileSize } from '../../../../utils/file';
import { DateFormat } from '../../../../utils/date';
import useToast from '../../../../chat/hooks/useToast';
import { StyledEnlarger } from '../../../../chat/components/MessagePreview/components/Image/styles';
import DocumentItemMenu from '../DocumentItemMenu';
import {

  DocumentRow,
  ThumbnailContainer,
  ThumbnailWrapper,
  ThumbnailImage,
  VideoThumbnail,
  ThumbnailIcon,
  VideoPreviewContent,
  VideoPlayer,
  SavedBadge,
  NameCell,
  NameRow,
  FileMetaLabel,
  FileName,
  RenameInput,
  UploadedCell,
  UploadedDate,
  UploadedBy,
} from './styles';
import texts from './texts.json';
import { TYPE_ICON, getTypeLabel } from './constants';

const ClientDocumentItem = ({ document, tags, deleteTexts }) => {
  const {
    fileName,
    mimeType,
    type,
    uploadedByName,
    uploadedAt,
    assetPath,
    fileSize,
    source,
    isSaved,
  } = document;

  const { getDownloadUrlFromStoragePath } = useStorage();
  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();

  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(fileName);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isRenaming) setRenameValue(fileName);
  }, [fileName, isRenaming]);

  useEffect(() => {
    if ((type !== FileType.IMAGE && type !== FileType.VIDEO) || !assetPath) return;
    getDownloadUrlFromStoragePath(assetPath)
      .then((url) => {
        if (isComponentMountedRef.current) setThumbnailUrl(url);
      })
      .catch(() => {});
  }, [assetPath, type, getDownloadUrlFromStoragePath, isComponentMountedRef]);

  const uploadedAtFormatted = uploadedAt
    ? moment(uploadedAt).format(DateFormat.SHORT_MONTH_DAY_COMMA_YEAR_FORMAT)
    : '';

  const fetchUrl = useCallback(() => {
    if ((type === FileType.IMAGE || type === FileType.VIDEO) && thumbnailUrl) return Promise.resolve(thumbnailUrl);
    return getDownloadUrlFromStoragePath(assetPath);
  }, [type, thumbnailUrl, getDownloadUrlFromStoragePath, assetPath]);

  const handleRowClick = useCallback(async () => {
    if (!assetPath) return;
    try {
      if (type === FileType.PDF || type === FileType.FILE) {
        const url = await fetchUrl();
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }
      if (type === FileType.IMAGE || type === FileType.VIDEO) {
        const url = await fetchUrl();
        setPreviewUrl(url);
        setIsPreviewOpen(true);
      }
    } catch {
      showToast(texts.previewError, { error: true });
    }
  }, [assetPath, type, fetchUrl, showToast]);

  const handleRenameStart = useCallback(() => {
    setRenameValue(fileName);
    setIsRenaming(true);
  }, [fileName]);

  const handleRenameSubmit = useCallback(async () => {
    const trimmed = renameValue.trim();
    if (!trimmed) {
      showToast(texts.emptyError, { error: true });
      return;
    }
    if (trimmed !== fileName) {
      await document.rename(trimmed);
      showToast(texts.fileRenamed);
    }
    setIsRenaming(false);
  }, [renameValue, fileName, document, showToast]);

  const handleRenameInputChange = useCallback((e) => setRenameValue(e.target.value), []);

  const handleRenameKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter') handleRenameSubmit();
      if (event.key === 'Escape') setIsRenaming(false);
    },
    [handleRenameSubmit],
  );

  const handleRenameBlur = useCallback(
    (e) => {
      if (e.relatedTarget?.dataset?.renameSubmit) return;
      handleRenameSubmit();
    },
    [handleRenameSubmit],
  );

  const renderThumbnailContent = useCallback(() => {
    if (type === FileType.IMAGE && thumbnailUrl) {
      return <ThumbnailImage src={thumbnailUrl} alt={fileName} />;
    }
    if (type === FileType.VIDEO && thumbnailUrl) {
      return <VideoThumbnail src={`${thumbnailUrl}#t=0.5`} preload="metadata" muted />;
    }
    return <ThumbnailIcon $type={type || FileType.FILE}>{TYPE_ICON[type] || TYPE_ICON[FileType.FILE]}</ThumbnailIcon>;
  }, [fileName, type, thumbnailUrl]);

  return (
    <>
      <DocumentRow>
        <ThumbnailContainer>
          <ThumbnailWrapper onClick={handleRowClick}>
            {renderThumbnailContent()}
          </ThumbnailWrapper>
          {isSaved && <SavedBadge />}
        </ThumbnailContainer>
        <NameCell>
          {isRenaming ? (
            <RenameInput
              ref={inputRef}
              value={renameValue}
              onChange={handleRenameInputChange}
              onBlur={handleRenameBlur}
              onKeyDown={handleRenameKeyDown}
              autoFocus
            />
          ) : (
            <>
              <NameRow>
                <FileName title={fileName}>{fileName}</FileName>
                {/*
                  Tags supplied by the caller replace the source tag rather than sitting
                  beside it: where a row already says which libraries hold the file,
                  "Upload" adds nothing a coach did not already know.
                */}
                {tags.length
                  ? tags.map((tag) => <InfoTag key={tag}>{tag}</InfoTag>)
                  : source === ClientDocumentSource.UPLOAD && <InfoTag>{texts.sourceUpload}</InfoTag>}
              </NameRow>
              <FileMetaLabel>{`${getTypeLabel(type)} · ${formatFileSize(fileSize)}`}</FileMetaLabel>
            </>
          )}
        </NameCell>
        <UploadedCell>
          <UploadedDate>{uploadedAtFormatted}</UploadedDate>
          {uploadedByName && <UploadedBy>{uploadedByName}</UploadedBy>}
        </UploadedCell>
        <DocumentItemMenu
          document={document}
          isRenaming={isRenaming}
          onRenameStart={handleRenameStart}
          onRenameSubmit={handleRenameSubmit}
          deleteTexts={deleteTexts}
        />
      </DocumentRow>
      {type === FileType.IMAGE && previewUrl && (
        <StyledEnlarger open={isPreviewOpen} onClick={() => setIsPreviewOpen(false)} placement="left-end">
          <img src={previewUrl} alt={fileName} />
        </StyledEnlarger>
      )}
      {type === FileType.VIDEO && (
        <Dialog open={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} maxWidth="md" fullWidth>
          <VideoPreviewContent>
            {previewUrl && (
              <VideoPlayer controls autoPlay>
                <source src={previewUrl} type={mimeType} />
                {mimeType !== 'video/mp4' && <source src={previewUrl} type="video/mp4" />}
              </VideoPlayer>
            )}
          </VideoPreviewContent>
        </Dialog>
      )}
    </>
  );
};

ClientDocumentItem.propTypes = {
  /*
    Not instanceOf(ClientDocument): Library Documents render the same row from
    product/{id}/libraryDocs, a different collection with the same surface. The
    shape is what keeps those two from drifting — a renamed getter on either side
    fails here rather than rendering blank.
  */
  document: PropTypes.shape({
    fileName: PropTypes.string,
    mimeType: PropTypes.string,
    type: PropTypes.string,
    uploadedAt: PropTypes.instanceOf(Date),
    uploadedBy: PropTypes.string,
    uploadedByName: PropTypes.string,
    assetPath: PropTypes.string,
    fileSize: PropTypes.number,
    source: PropTypes.string,
    isSaved: PropTypes.bool,
    rename: PropTypes.func,
    remove: PropTypes.func,
    toggleSaved: PropTypes.func,
  }).isRequired,
  /** Shown beside the file name in place of the source tag. */
  tags: PropTypes.arrayOf(PropTypes.string),
  /** Overrides for the delete confirmation, passed through to the menu. */
  deleteTexts: PropTypes.shape({
    title: PropTypes.string,
    content: PropTypes.string,
    confirm: PropTypes.string,
    cancel: PropTypes.string,
  }),
};

ClientDocumentItem.defaultProps = {
  tags: [],
  deleteTexts: {},
};

export default observer(ClientDocumentItem);
